import { MedplumClient } from '@medplum/core'
import { useStore } from 'store'
import { captureException, SentryLevel, SentrySources, TransactionNames } from 'utils/sentry'
import { getErrorInstance, getGraphQLErrorMessage } from './helpers'

export const ALLOWED_STATUS_CODES = [200, 201, 202, 203, 204, 205, 206, 208, 226, 401, 403]

export const ALLOWED_MEDPLUM_ERRORS = ['Failed to fetch', 'Load failed']

/**
 * Helper to get session state from store safely.
 * Returns undefined if store is not available (e.g., in tests without proper mock).
 */
const getSessionState = ():
  | { isFeatureEnabled: boolean; setExpired: (v: boolean) => void }
  | undefined => {
  try {
    return useStore.getState()?.session
  } catch {
    return undefined
  }
}

const fetchMiddleware = async (url: string, options?: RequestInit): Promise<Response> => {
  let response: Response
  try {
    response = await fetch(url, options)

    // Detect token/user issues from body and tag accordingly
    if (response?.status && !ALLOWED_STATUS_CODES.includes(response.status)) {
      let isInvalidToken = false
      let isUserNotFound = false
      const responseClone = response.clone()
      const rawBody = await responseClone.text()
      if (rawBody) {
        try {
          const parsed = JSON.parse(rawBody)
          // Check for invalid token or invalid refresh token errors (Medplum returns 400 with these)
          const errorDesc = parsed?.error_description || ''
          isInvalidToken = errorDesc === 'Invalid token' || errorDesc === 'Invalid refresh token'
          isUserNotFound = parsed?.issue?.[0]?.details?.text === 'User not found'
        } catch (_) {
          /* ignore json parse */
        }
      }

      // Handle session expiration - Medplum returns invalid token errors in response body
      const session = getSessionState()
      if (isInvalidToken && session?.isFeatureEnabled) {
        session.setExpired(true)
        return response
      }

      const text = rawBody.length > 2000 ? `${rawBody.slice(0, 2000)}…[truncated]` : rawBody

      // Determine source tag and level based on error type
      const infoSentryIssue = isInvalidToken || isUserNotFound
      let source = SentrySources.MEDPLUM_FETCH_MIDDLEWARE
      if (isInvalidToken) {
        source = SentrySources.MEDPLUM_TOKEN_ISSUE
      } else if (isUserNotFound) {
        source = SentrySources.MEDPLUM_USER_NOT_FOUND
      }

      // Create distinct error messages so Sentry groups them as separate issues
      let errorMessage = `Fetch error with status ${response.status}`
      if (isInvalidToken) {
        errorMessage = `Medplum: Invalid token (status ${response.status})`
      } else if (isUserNotFound) {
        errorMessage = `Medplum: User not found (status ${response.status})`
      }

      captureException(new Error(errorMessage), {
        tags: { source, url },
        extras: { params: options, info: text },
        transactionName: TransactionNames.FETCH,
        level: infoSentryIssue ? SentryLevel.INFO : undefined
      })
    }
    return response
  } catch (error) {
    if (!ALLOWED_MEDPLUM_ERRORS.some((allowedError) => error.message.includes(allowedError))) {
      captureException(getErrorInstance(error), {
        tags: { source: SentrySources.MEDPLUM_FETCH_MIDDLEWARE, url },
        extras: {
          params: options,
          errors: [error],
          info: typeof error?.message === 'string' ? error.message : undefined
        },
        transactionName: TransactionNames.FETCH
      })
    }

    throw error
  }
}

class MedplumClientMiddleware extends MedplumClient {
  async graphql(
    query: string,
    operationName?: string | null,
    variables?: Record<string, unknown>,
    options?: RequestInit
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<any> {
    try {
      const result = await super.graphql(query, operationName, variables, options)

      const { data, errors, error } = result

      const errorMessage = `A Graphql query error is returned from Medplum: ${getGraphQLErrorMessage(
        errors
      )}`

      if (errors || error) {
        captureException(new Error(errorMessage), {
          tags: { source: SentrySources.MEDPLUM_GRAPHQL_QUERY },
          extras: {
            info: {
              result: data
            },
            errors: errors || [{ message: error }],
            query: { query, operationName, variables }
          }
        })
      }

      return result
    } catch (error) {
      throw new Error('Error in useQuery', { cause: error })
    }
  }
}

export const medplum = new MedplumClientMiddleware({
  // Use Next.js fetch
  baseUrl: process.env.NEXT_PUBLIC_MEDPLUM_BASE_URL,
  fetch: async (url: string, options?: RequestInit) => await fetchMiddleware(url, options),
  cacheTime: 0
})
